import { useEffect, useState } from "react";
import { Button, Dropdown, Result, Space, Table, Col, Row } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";

import api from "../../../services/stemiService";
import DownloadOverviewResults from "./DownloadOverviewResults";
import { FileGeneratorProgress } from '@seims/shared';


function Feedback({ file, setProgressState, data }) {
  const [message, setMessage] = useState("File Upload was successful");
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [feedBackData, setFeedBackData] = useState([]);
  const [successful, setSuccessful] = useState(false);
  const [error, setError] = useState(false);
  const [fileUUID, setFileUUID] = useState("");
  const [reportData, setReportData] = useState([]);
  const [showPDF, setShowPDF] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState("Generating file, please wait...");
  
  useEffect(() => {
    if (data.overallStatus === "Data Upload Failed") {
      setMessage("One or more columns failed to meet upload criterion");
      setSuccessful(false);
    } else if (data.overallStatus === "Data Upload Successful") {
      setMessage("All columns met upload criterion");
      setSuccessful(true);
    } else {
      setMessage(
        "Something unexpected happened while processing the file, please reupload..."
      );
      setSuccessful(false);
      setError(true);
    }

    setFileUUID(data.fileUUID);
    const _feedBackData = data.headerReport?.map((feedback, key) => ({
      key: key,
      status: feedback.headerStatus,
      checklist: feedback.checkList,
    }));

    setFeedBackData(_feedBackData);
  }, []);

  const onBack = () => {
    setProgressState(0);
  };

   const onReset = () => {
    setProgress(0);
    setProgressText("Generating file, please wait...");
  };


  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "1",
      render: (value) =>
        value === "Data Upload Successful" ? (
          <CheckOutlined
            style={{
              fontSize: "18px",
              color: "#04a596",
              fontWeight: "bold",
            }}
          />
        )
          : value === "Data Upload Failed" ? (
            <CloseOutlined
              style={{
                fontSize: "18px",
                color: "#a21b1b",
                fontWeight: "bold",
              }}
            />
          )
            : <WarningOutlined
              style={{
                fontSize: "18px",
                color: "#a21b1b",
                fontWeight: "bold",
              }}
            />
    },
    {
      title: "Checklist",
      dataIndex: "checklist",
      key: "2",
    },
  ];

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  const onDownloadExcel = async (data) => {
    const { details: reportData, fileDetails } = data;
    const { filename } = fileDetails;
    let commentCount = 0;

      await new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      setIsGenerating(true);

      fileReader.readAsArrayBuffer(file);
      fileReader.onload = async (e) => {
        const bufferArray = e.target.result;
        const wb = XLSX.read(bufferArray, { type: "buffer" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        let colAlias = "";

        for (let rowIndex = 0; rowIndex < reportData.length; rowIndex++) {
          //START FROM COLUMN A (StemiName) - COLUMN R (LEARNER RESULTS)
          if (reportData[rowIndex].col === 0) colAlias = "A";
          if (reportData[rowIndex].col === 1) colAlias = "B";
          if (reportData[rowIndex].col === 2) colAlias = "C";
          if (reportData[rowIndex].col === 3) colAlias = "D";
          if (reportData[rowIndex].col === 4) colAlias = "E";
          if (reportData[rowIndex].col === 5) colAlias = "F";
          if (reportData[rowIndex].col === 6) colAlias = "G";
          if (reportData[rowIndex].col === 7) colAlias = "H";
          if (reportData[rowIndex].col === 8) colAlias = "I";
          if (reportData[rowIndex].col === 9) colAlias = "J";
          if (reportData[rowIndex].col === 10) colAlias = "K";
          if (reportData[rowIndex].col === 11) colAlias = "L";
          if (reportData[rowIndex].col === 12) colAlias = "M";
          if (reportData[rowIndex].col === 13) colAlias = "N";
          if (reportData[rowIndex].col === 14) colAlias = "O";
          if (reportData[rowIndex].col === 15) colAlias = "P";
          if (reportData[rowIndex].col === 16) colAlias = "Q";
          if (reportData[rowIndex].col === 17) colAlias = "R";

          const currRow = reportData[rowIndex].row + 1;
          const cell = ws[colAlias + currRow];

          if (cell) {
            XLSX.utils.cell_add_comment(
              cell,
              reportData[rowIndex].message,
              "CSIR"
            );
          }
        }

        const range = XLSX.utils.decode_range(ws["!ref"]);

        if (range.e.r > 0){
          setRecordCount(range.e.r);
        }

        for (let i = range.s.r; i <= range.e.r; i++) {
          if ((i / range.e.r) * 100 < 99) {
            await new Promise((resolve) => setTimeout(resolve, 0));
            setProgress(Math.round((i / range.e.r) * 100));
          }

          for (let j = range.s.c; j <= 18; j++) {
            const cell = ws[XLSX.utils.encode_cell({ r: i, c: j })];

            const errorFound = reportData.filter(
              (current) => current.row === i && current.col === j
              );
              
              if (cell && errorFound.length > 0) {
                cell.s = { fill: { fgColor: { rgb: "ffff00" } } };
            }
          }
        }
        setProgressText("Downloading file...");

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, ws, wsname);

        const fileInfo = filename.split(".");
        const lastIndex = fileInfo.length - 1;
        XLSX.writeFile(
          wb,
          `${fileInfo[0]}_WITH_COMMENTS.${fileInfo[lastIndex]}`
        );


        setProgress(100);

        if (data.length > 0) {
          resolve(data);
          setProgressText("Done");

          if (!isGenerating)
          {
            onReset();
          }
        } else {
        }
        setIsGenerating(false);

      };
      FileReader.onerror = (error) => {
        reject(error);
        setLoading(true);
      };
    });

  };

  const items = [
    {
      label: "PDF - Overview",
      key: "1",
      icon: <FilePdfOutlined />,
    },
    {
      label: "EXCEL - Detailed",
      key: "2",
      icon: <FileExcelOutlined />,
      disabled: successful,
    },
  ];

  const handleButtonClick = (e) => {

   };

  const handleMenuClick = async (e) => {

    const response = await api.downloadReport(fileUUID);
    if (response && response.status === 200) {
      const { data } = response;

      if (e.key == 1) {
        const overviewResponse = await api.downloadOverviewReport(fileUUID);
        setShowPDF(true);
      } else if (e.key == 2) {
        onDownloadExcel(data);
      }
      // setUploadErrors(details);
      setReportData(data);
    }
  };

  const handleCancel = () => {
    setShowPDF(false);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <div>
      <Result
        status={successful ? "success" : "error"}
        title={message}
        extra={[]}
      />
      <Table
        dataSource={feedBackData}
        columns={columns}
        loading={loading}
        rowKey={(record) => record.key}
        bordered
        pagination={{ pageSize: pageSize }}
        size="middle"
        onChange={onRowChange}
      />
      <br />
      <Row>
      <Col xs={24} className="right-align">
      <div>
        <Button key="back" style={{ marginRight: "10px" }} onClick={onBack}>
          Back
        </Button>
        {!error && (
          <Dropdown menu={menuProps} onClick={handleButtonClick}>
            <Space>
              <Button type="primary">
                Download Results
                <DownloadOutlined />
              </Button>
            </Space>
          </Dropdown>
        )}
      </div>
      <DownloadOverviewResults
        show={showPDF} 
        onCancel={handleCancel}
        successful={successful}
        data={reportData}
      />
      </Col>
    </Row>
    <FileGeneratorProgress progressText={progressText} progress={progress} isGenerating={isGenerating} />
    
    </div>
    
  );
}

export default Feedback;
